import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query'
import { Archive, ArchiveRestore, Pencil, Plus, Trash2 } from 'lucide-react'
import { type FormEvent, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'

import { useIsOrganizationAdmin } from '@/features/auth/use-organization-role'
import {
  archiveProject,
  deleteProject,
  listPrompts,
  listProjects,
  listProjectTags,
  unarchiveProject,
  updateProject,
  type UpsertProjectInput,
} from '@/features/projects/projects-api'
import { CreateProjectModal } from '@/features/projects/components/create-project-modal'
import { ProjectTags } from '@/features/projects/components/project-tags'
import { TagFilterBar } from '@/features/projects/components/tag-filter-bar'
import { ProjectProvisioningState } from '@/features/projects/components/project-provisioning-state'
import {
  getArchiveBadgeLabel,
  getInactivityDaysBadge,
  getProjectPromptsCount,
  getProjectProvisioningState,
  hasProjectPromptsCount,
} from '@/features/projects/project-utils'
import {
  archivedProjectsQueryKey,
  projectPromptsQueryKey,
  projectsQueryKey,
  projectTagsQueryKey,
} from '@/features/projects/projects-query-keys'
import {
  unreadConversationsQueryKey,
  useUnreadConversations,
} from '@/features/projects/use-unread-conversations'
import { EmptyState } from '@/shared/components/common/empty-state'
import { LoadingBlock } from '@/shared/components/common/loading-block'
import { PageHeader } from '@/shared/components/common/page-header'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Card, CardBody, CardDescription, CardHeader, CardTitle } from '@/shared/components/ui/card'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { ConfirmDialog } from '@/shared/components/ui/confirm-dialog'
import { CountBadge } from '@/shared/components/ui/count-badge'
import { Field } from '@/shared/components/ui/field'
import { Input } from '@/shared/components/ui/input'
import { Modal } from '@/shared/components/ui/modal'
import { TabButton, TabsList } from '@/shared/components/ui/tabs'
import { Textarea } from '@/shared/components/ui/textarea'
import { formatDate } from '@/shared/utils/date'
import type { Project } from '@/shared/types/domain'

// Тот же ключ, что и в project-layout.tsx / project-settings-tab.tsx и др.: карточку проекта
// каждый экран инвалидирует своим локальным ['project', id], общей константы под неё нет.
const projectQueryKey = (projectId: string) => ['project', projectId] as const

interface ProjectFormState {
  id?: string
  name: string
  description: string
}

function toProjectInput(form: ProjectFormState): UpsertProjectInput {
  return {
    name: form.name.trim(),
    description: form.description.trim(),
  }
}

export function ProjectsPage() {
  const queryClient = useQueryClient()
  const { unreadForProject } = useUnreadConversations()
  // Создание проекта требует роли admin в активной организации: в чужой организации
  // (там пользователь member) сервер отвечает 403, кнопку показывать незачем.
  const canCreateProject = useIsOrganizationAdmin()
  const [modalOpen, setModalOpen] = useState(false)
  const [form, setForm] = useState<ProjectFormState>({ name: '', description: '' })
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null)
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [archiveTarget, setArchiveTarget] = useState<Project | null>(null)

  const [searchParams, setSearchParams] = useSearchParams()

  // Вкладка «Архив» — тот же GET /projects с ?archived=only и отдельным ключом кеша: под одним
  // ключом с активным списком результаты двух разных запросов схлопнулись бы в один кеш.
  const view = searchParams.get('view') === 'archived' ? 'archived' : 'active'

  const setView = (next: 'active' | 'archived') => {
    const params = new URLSearchParams(searchParams)

    if (next === 'archived') {
      params.set('view', 'archived')
      // Справочник тегов посчитан только по активным проектам (бэкенд ограничивает выборку
      // видимыми) — перенесённый фильтр на архиве либо режет по тегу, которого там нет ни у
      // кого, либо считает по чужим числам. Проще не тащить его на вкладку вовсе.
      params.delete('tags')
    } else {
      params.delete('view')
    }

    setSearchParams(params, { replace: true })
    // Режим выбора живёт только на «Активных»: смена вкладки без явного «Готово»
    // не должна оставлять подвисший selectMode/selectedIds.
    setSelectMode(false)
    setSelectedIds(new Set())
  }

  const projectsQuery = useQuery({
    queryKey: view === 'archived' ? archivedProjectsQueryKey : projectsQueryKey,
    queryFn: () => (view === 'archived' ? listProjects({ archived: 'only' }) : listProjects()),
    // Пока хоть один проект собирается, список перезапрашивается сам — иначе
    // готовый проект остаётся заглушкой до перезагрузки страницы.
    refetchInterval: (query) =>
      (query.state.data || []).some((project) => getProjectProvisioningState(project))
        ? 12_000
        : false,
  })

  const tagsQuery = useQuery({
    queryKey: projectTagsQueryKey,
    queryFn: listProjectTags,
  })

  // Id из адреса могут устареть: тег сняли с последнего проекта или пользователь
  // переключил организацию, а ссылка осталась. Неизвестные молча выбрасываем.
  const selectedTagIds = useMemo(() => {
    const known = new Set((tagsQuery.data || []).map((tag) => tag.id))

    return (searchParams.get('tags') || '')
      .split(',')
      .filter((id) => id.length > 0 && known.has(id))
  }, [searchParams, tagsQuery.data])

  const applyTagFilter = (ids: string[]) => {
    const next = new URLSearchParams(searchParams)

    if (ids.length > 0) {
      next.set('tags', ids.join(','))
    } else {
      next.delete('tags')
    }

    setSearchParams(next, { replace: true })
  }

  const toggleTag = (tagId: string) => {
    applyTagFilter(
      selectedTagIds.includes(tagId)
        ? selectedTagIds.filter((id) => id !== tagId)
        : [...selectedTagIds, tagId],
    )
  }

  const visibleProjects = useMemo(() => {
    const projects = projectsQuery.data || []

    // Фильтр по тегам не действует на «Архиве»: справочник тегов посчитан только по
    // активным проектам, поэтому на архивных он либо режет по несуществующему тегу,
    // либо считает по чужим числам (прямая ссылка ?view=archived&tags=... — тот же случай).
    if (view === 'archived' || selectedTagIds.length === 0) return projects

    // Логика ИЛИ: проект попадает в выборку, если у него есть любой из выбранных тегов.
    return projects.filter((project) =>
      (project.tags || []).some((tag) => selectedTagIds.includes(tag.id)),
    )
  }, [projectsQuery.data, selectedTagIds, view])

  const projectsMissingPromptCounts = useMemo(
    () => (projectsQuery.data || []).filter((project) => !hasProjectPromptsCount(project)),
    [projectsQuery.data],
  )

  const promptCountQueries = useQueries({
    queries: projectsMissingPromptCounts.map((project) => ({
      queryKey: projectPromptsQueryKey(project.id),
      queryFn: () => listPrompts(project.id),
    })),
  })

  const promptCountByProjectId = useMemo(() => {
    const countByProjectId = new Map<string, number>()

    projectsMissingPromptCounts.forEach((project, index) => {
      const prompts = promptCountQueries[index]?.data

      if (prompts) {
        countByProjectId.set(project.id, prompts.length)
      }
    })

    return countByProjectId
  }, [projectsMissingPromptCounts, promptCountQueries])

  const saveProjectMutation = useMutation({
    mutationFn: async (input: ProjectFormState) => {
      if (!input.id) return

      return updateProject(input.id, toProjectInput(input))
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectsQueryKey })
      setModalOpen(false)
      setForm({ name: '', description: '' })
    },
  })

  const deleteProjectMutation = useMutation({
    mutationFn: deleteProject,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: projectsQueryKey })
      setDeleteTarget(null)
    },
  })

  const archiveMutation = useMutation({
    mutationFn: (projectId: string) => archiveProject(projectId),
    onSuccess: (updated) => {
      queryClient.setQueryData<Project[]>(projectsQueryKey, (projects) =>
        projects?.filter((item) => item.id !== updated.id),
      )
      // Корневой ключ ['projects'], а не точный ключ активного списка: под тем же
      // префиксом сидят переключатель проектов в шапке и фильтр лидов — узкая
      // инвалидация оставила бы заархивированный проект у них на виду.
      void queryClient.invalidateQueries({ queryKey: projectsQueryKey })
      void queryClient.invalidateQueries({ queryKey: unreadConversationsQueryKey })
      // Иначе открытая страница проекта (свой кеш ['project', id], staleTime 30с) ещё
      // полминуты показывает старое состояние — полосы «в архиве» нет, хотя бот уже молчит.
      void queryClient.invalidateQueries({ queryKey: projectQueryKey(updated.id) })
      setArchiveTarget(null)
      toast.success('Проект архивирован')
    },
  })

  const unarchiveMutation = useMutation({
    mutationFn: (projectId: string) => unarchiveProject(projectId),
    onSuccess: (updated) => {
      queryClient.setQueryData<Project[]>(archivedProjectsQueryKey, (projects) =>
        projects?.filter((item) => item.id !== updated.id),
      )
      void queryClient.invalidateQueries({ queryKey: projectsQueryKey })
      void queryClient.invalidateQueries({ queryKey: unreadConversationsQueryKey })
      // Симметрично архивации: иначе открытая страница проекта ещё полминуты держит
      // полосу «в архиве» с кнопкой возврата у уже живого проекта.
      void queryClient.invalidateQueries({ queryKey: projectQueryKey(updated.id) })
      toast.success('Проект возвращён из архива')
    },
  })

  const [selectMode, setSelectMode] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false)

  const toggleSelected = (projectId: string) => {
    setSelectedIds((current) => {
      const next = new Set(current)
      if (next.has(projectId)) next.delete(projectId)
      else next.add(projectId)
      return next
    })
  }

  const bulkArchiveMutation = useMutation({
    mutationFn: async (targets: { id: string; name: string }[]) => {
      // ponytail: N последовательных запросов к обычной ручке POST /projects/{id}/archive —
      // у клиента единицы-десятки проектов за раз, а bulk-эндпоинт со своей валидацией и
      // частичными отказами заводить только когда организации станут архивировать сотнями и
      // последовательный обход станет заметно медленным.
      const succeeded: { id: string; name: string }[] = []
      const failed: { id: string; name: string }[] = []

      for (const target of targets) {
        try {
          // suppressErrorToast: итог пачки уже честно показываем сами ниже — общий
          // тост на каждый упавший запрос был бы лавиной поверх него.
          await archiveProject(target.id, { suppressErrorToast: true })
          succeeded.push(target)
        } catch {
          failed.push(target)
        }
      }

      return { succeeded, failed }
    },
    onSuccess: (result) => {
      const succeededIds = new Set(result.succeeded.map((target) => target.id))
      queryClient.setQueryData<Project[]>(projectsQueryKey, (projects) =>
        projects?.filter((item) => !succeededIds.has(item.id)),
      )
      void queryClient.invalidateQueries({ queryKey: projectsQueryKey })
      void queryClient.invalidateQueries({ queryKey: unreadConversationsQueryKey })
      // По каждому успешно заархивированному — иначе его страница проекта, если открыта
      // в другой вкладке, ещё полминуты не покажет полосу «в архиве».
      result.succeeded.forEach((target) => {
        void queryClient.invalidateQueries({ queryKey: projectQueryKey(target.id) })
      })
      setSelectMode(false)
      setSelectedIds(new Set())
      setBulkConfirmOpen(false)

      const total = result.succeeded.length + result.failed.length
      const options =
        result.failed.length > 0
          ? { description: `Не удалось: ${result.failed.map((target) => target.name).join(', ')}` }
          : undefined

      if (result.failed.length === 0) {
        toast.success(`Заархивировано: ${total}`, options)
      } else {
        toast.error(`Заархивировано ${result.succeeded.length} из ${total}`, options)
      }
    },
  })

  // Список каналов, которые замолчат: строкой — только то, что у проекта реально включено.
  const archiveChannels = archiveTarget
    ? [
        archiveTarget.widget_enabled ? 'Виджет на сайте' : null,
        archiveTarget.telegram_enabled ? 'Уведомления в Telegram' : null,
        (archiveTarget.active_avito_listings_count ?? 0) > 0
          ? `объявлений на Авито: ${archiveTarget.active_avito_listings_count}`
          : null,
      ]
        .filter(Boolean)
        .join(', ')
    : ''

  const archiveDescription = archiveChannels
    ? `Бот перестанет отвечать, диалоги и лиды пропадут из списков до возврата. Замолчит: ${archiveChannels}.`
    : 'Бот перестанет отвечать, диалоги и лиды пропадут из списков до возврата.'

  const subtitle = useMemo(() => {
    const total = projectsQuery.data?.length ?? 0

    if (selectedTagIds.length > 0) {
      return `Показано: ${visibleProjects.length} из ${total}`
    }

    return `Всего проектов: ${total}`
  }, [projectsQuery.data, selectedTagIds, visibleProjects])

  const openCreate = () => {
    setCreateModalOpen(true)
  }

  const openEdit = (id: string, name: string, description: string | null) => {
    setForm({ id, name, description: description || '' })
    setModalOpen(true)
  }

  const onSave = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (!form.name.trim()) {
      return
    }

    await saveProjectMutation.mutateAsync(form)
  }

  return (
    <div>
      <PageHeader
        actions={
          canCreateProject ? (
            <Button onClick={openCreate}>
              <Plus className="h-4 w-4" />
              Новый проект
            </Button>
          ) : (
            <span className="text-sm text-[var(--color-text-soft)]">
              Создавать проекты может только администратор организации
            </span>
          )
        }
        subtitle={subtitle}
        title="Проекты"
      />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <TabsList>
          <TabButton active={view === 'active'} onClick={() => setView('active')}>
            Активные
          </TabButton>
          <TabButton active={view === 'archived'} onClick={() => setView('archived')}>
            Архив
          </TabButton>
        </TabsList>

        {view === 'active' ? (
          <Button
            onClick={() => {
              setSelectMode((current) => !current)
              setSelectedIds(new Set())
            }}
            size="sm"
            variant={selectMode ? 'secondary' : 'ghost'}
          >
            {selectMode ? 'Готово' : 'Выбрать'}
          </Button>
        ) : null}
      </div>

      {view === 'active' ? (
        <TagFilterBar
          onReset={() => applyTagFilter([])}
          onToggle={toggleTag}
          selectedIds={selectedTagIds}
          tags={tagsQuery.data || []}
        />
      ) : null}

      {selectMode && view === 'active' ? (
        <Card className="mb-4">
          <CardBody className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            {selectedIds.size > 0 ? (
              <span className="text-sm font-medium">Выбрано: {selectedIds.size}</span>
            ) : (
              <span className="text-sm text-[var(--color-text-muted)]">
                Отметьте проекты, чтобы архивировать несколько сразу
              </span>
            )}
            <Button
              disabled={selectedIds.size === 0}
              onClick={() => setBulkConfirmOpen(true)}
              size="sm"
              variant="secondary"
            >
              <Archive className="h-4 w-4" />В архив ({selectedIds.size})
            </Button>
          </CardBody>
        </Card>
      ) : null}

      {projectsQuery.isLoading ? (
        <LoadingBlock label="Загружаем проекты..." />
      ) : visibleProjects.length > 0 ? (
        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {visibleProjects.map((project) => {
            if (project.archived_at) {
              return (
                <Card className="opacity-70" key={project.id}>
                  <CardHeader>
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <CardTitle className="text-base">{project.name}</CardTitle>
                        <CardDescription className="line-clamp-2 max-w-md">
                          {project.description || 'Описание пока не добавлено'}
                        </CardDescription>
                      </div>
                      <Badge tone="neutral">{getArchiveBadgeLabel(project)}</Badge>
                    </div>
                  </CardHeader>

                  <CardBody className="space-y-4">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link className="flex-1" to={`/projects/${project.id}`}>
                        <Button className="w-full" variant="secondary">
                          Открыть
                        </Button>
                      </Link>
                      <Button
                        loading={
                          unarchiveMutation.isPending && unarchiveMutation.variables === project.id
                        }
                        onClick={() => unarchiveMutation.mutate(project.id)}
                        variant="secondary"
                      >
                        <ArchiveRestore className="h-4 w-4" />
                        Вернуть из архива
                      </Button>
                    </div>
                  </CardBody>
                </Card>
              )
            }

            const provisioningState = getProjectProvisioningState(project)
            const inactivityDays = getInactivityDaysBadge(project)

            return provisioningState ? (
              <ProjectProvisioningState
                action={
                  <>
                    {provisioningState === 'provisioning' ? (
                      <Link className="flex-1" to={`/projects/${project.id}`}>
                        <Button className="w-full" variant="secondary">
                          Посмотреть статус
                        </Button>
                      </Link>
                    ) : null}
                    {/* Удаление здесь — запасной выход, а не главное действие: тихий ghost. */}
                    <Button
                      aria-label={`Удалить проект ${project.name}`}
                      onClick={() => setDeleteTarget({ id: project.id, name: project.name })}
                      variant="ghost"
                    >
                      <Trash2 className="h-4 w-4" />
                      {provisioningState === 'blocked' ? 'Удалить проект' : null}
                    </Button>
                  </>
                }
                blockReason={project.provisioning_block_reason}
                key={project.id}
                status={provisioningState}
                title={project.name}
              />
            ) : (
              <Card key={project.id}>
                <CardHeader>
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-2">
                      {selectMode ? (
                        <Checkbox
                          aria-label={`Выбрать проект ${project.name}`}
                          checked={selectedIds.has(project.id)}
                          className="mt-1"
                          onChange={() => toggleSelected(project.id)}
                        />
                      ) : null}
                      <div className="min-w-0">
                        <CardTitle className="text-base">{project.name}</CardTitle>
                        <CardDescription className="line-clamp-2 max-w-md">
                          {project.description || 'Описание пока не добавлено'}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex flex-none items-center gap-2">
                      <CountBadge count={unreadForProject(project.id)} />
                      {inactivityDays !== null ? (
                        <Badge tone="warning">Без диалогов: {inactivityDays} дн.</Badge>
                      ) : null}
                      <Badge>
                        {getProjectPromptsCount(project, promptCountByProjectId.get(project.id))}{' '}
                        промптов
                      </Badge>
                    </div>
                  </div>
                </CardHeader>

                <CardBody className="space-y-4">
                  <dl className="space-y-1 text-sm text-[var(--color-text-soft)]">
                    <div className="flex items-center justify-between gap-4">
                      <dt>Создан:</dt>
                      <dd className="font-medium text-[var(--color-text)]">
                        {formatDate(project.created_at)}
                      </dd>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <dt>Обновлён:</dt>
                      <dd className="font-medium text-[var(--color-text)]">
                        {formatDate(project.updated_at)}
                      </dd>
                    </div>
                  </dl>

                  <ProjectTags project={project} />

                  <div className="flex flex-wrap items-center gap-2">
                    <Link className="flex-1" to={`/projects/${project.id}`}>
                      <Button className="w-full" variant="secondary">
                        Открыть
                      </Button>
                    </Link>
                    <Button
                      onClick={() => openEdit(project.id, project.name, project.description)}
                      variant="ghost"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      aria-label={`Архивировать проект ${project.name}`}
                      onClick={() => setArchiveTarget(project)}
                      variant="ghost"
                    >
                      <Archive className="h-4 w-4" />
                    </Button>
                    <Button
                      onClick={() => setDeleteTarget({ id: project.id, name: project.name })}
                      variant="danger"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </CardBody>
              </Card>
            )
          })}
        </section>
      ) : (
        <EmptyState
          action={
            view === 'archived' ? null : selectedTagIds.length > 0 ? (
              <Button onClick={() => applyTagFilter([])} variant="secondary">
                Сбросить фильтр
              </Button>
            ) : canCreateProject ? (
              <Button onClick={openCreate}>
                <Plus className="h-4 w-4" />
                Создать первый проект
              </Button>
            ) : null
          }
          description={
            view === 'archived'
              ? 'Здесь появятся проекты, которые вы отправите в архив.'
              : selectedTagIds.length > 0
                ? 'Под выбранные теги не подошёл ни один проект.'
                : canCreateProject
                  ? 'Создайте проект и начните работать с промптами, диалогами и оценками файлов.'
                  : 'В этой организации вы участник: проекты создаёт её администратор.'
          }
          title={
            view === 'archived'
              ? 'В архиве пока пусто'
              : selectedTagIds.length > 0
                ? 'Ничего не найдено'
                : 'Пока нет проектов'
          }
        />
      )}

      <Modal
        description="Название обязательно, описание — опционально."
        onClose={() => setModalOpen(false)}
        open={modalOpen}
        title="Редактировать проект"
      >
        <form className="space-y-4" onSubmit={onSave}>
          <Field label="Название" required>
            {(props) => (
              <Input
                {...props}
                onChange={(event) =>
                  setForm((current) => ({ ...current, name: event.target.value }))
                }
                placeholder="Например: Quality QA"
                required
                value={form.name}
              />
            )}
          </Field>

          <Field label="Описание">
            {(props) => (
              <Textarea
                {...props}
                onChange={(event) =>
                  setForm((current) => ({ ...current, description: event.target.value }))
                }
                placeholder="Краткое описание задачи или контекста"
                value={form.description}
              />
            )}
          </Field>

          <div className="flex justify-end gap-2">
            <Button onClick={() => setModalOpen(false)} type="button" variant="ghost">
              Отмена
            </Button>
            <Button loading={saveProjectMutation.isPending} type="submit">
              Сохранить
            </Button>
          </div>
        </form>
      </Modal>

      <CreateProjectModal onClose={() => setCreateModalOpen(false)} open={createModalOpen} />

      <ConfirmDialog
        confirmLabel="Удалить"
        confirmText={deleteTarget?.name}
        description="Это действие необратимо. Все данные проекта будут удалены."
        loading={deleteProjectMutation.isPending}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => {
          if (deleteTarget) {
            deleteProjectMutation.mutate(deleteTarget.id)
          }
        }}
        open={deleteTarget !== null}
        title="Удалить проект?"
        tone="danger"
      />

      <ConfirmDialog
        confirmLabel="В архив"
        description={archiveDescription}
        loading={archiveMutation.isPending}
        onClose={() => setArchiveTarget(null)}
        onConfirm={() => {
          if (archiveTarget) {
            archiveMutation.mutate(archiveTarget.id)
          }
        }}
        open={archiveTarget !== null}
        title="Архивировать проект?"
      />

      <ConfirmDialog
        confirmLabel="В архив"
        description={`Будет заархивировано проектов: ${selectedIds.size}. Бот в них замолчит, диалоги и лиды скрыты до возврата.`}
        loading={bulkArchiveMutation.isPending}
        onClose={() => setBulkConfirmOpen(false)}
        onConfirm={() => {
          // Из полного списка, не из visibleProjects: смена фильтра по тегам не должна молча
          // выбрасывать уже отмеченные карточки из пачки. Проекты, которых уже нет в списке
          // (архивированы/удалены в другой вкладке), отсеиваются тем же filter — им просто не
          // находится совпадения.
          const targets = (projectsQuery.data || [])
            .filter((project) => selectedIds.has(project.id))
            .map((project) => ({ id: project.id, name: project.name }))
          bulkArchiveMutation.mutate(targets)
        }}
        open={bulkConfirmOpen}
        title="Архивировать выбранные проекты?"
      />
    </div>
  )
}
